// ABOUTME: Page for accepting workspace invitations via token-based invite links.
// ABOUTME: Fetches invite details by token, shows workspace info, and handles accept/decline actions.

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Loader2, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { trpc } from '@/lib/trpc';
import type { InviteDetails } from '@/types/workspace';

export function AcceptInvitePage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const { refreshWorkspaces, switchWorkspace } = useWorkspace();

    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Redirect to login if not authenticated
    const shouldRedirectToLogin = !authLoading && !user && !!token;
    if (shouldRedirectToLogin) {
        navigate(`/login?redirect=/invites/${token}/accept`);
    }

    // Fetch invite details via tRPC query
    const inviteQuery = trpc.invite.getByToken.useQuery(
        { token: token! },
        {
            enabled: !!token && !!user && !authLoading,
            select: (data) => ((data as any).invite ?? data) as unknown as InviteDetails,
            retry: false,
        }
    );

    const invite = inviteQuery.data ?? null;
    const loading = inviteQuery.isLoading;
    const fetchError = inviteQuery.error?.message ?? null;

    const acceptInviteMutation = trpc.invite.accept.useMutation();

    const handleAccept = async () => {
        if (!token) return;

        setAccepting(true);
        setError(null);

        try {
            const result = await acceptInviteMutation.mutateAsync({ token });
            setSuccess(true);

            // Refresh workspaces and switch to the new one
            await refreshWorkspaces();
            await switchWorkspace(result.workspaceId);

            // Redirect after a short delay to the main app
            setTimeout(() => {
                navigate('/app');
            }, 2000);
        } catch (err) {
            console.error('Failed to accept invite:', err);
            setError(err instanceof Error ? err.message : 'Failed to accept invite');
        } finally {
            setAccepting(false);
        }
    };

    const handleDecline = () => {
        navigate('/app');
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-muted-foreground">Loading invite...</p>
                </div>
            </div>
        );
    }

    if ((fetchError || error) && !invite) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 size-16 rounded-full bg-destructive/10 flex items-center justify-center">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle>Invalid Invite</CardTitle>
                        <CardDescription>{fetchError || error}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <Button onClick={() => navigate('/app')}>
                            Go to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 size-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <CardTitle>Welcome to {invite?.workspaceName}!</CardTitle>
                        <CardDescription>
                            You've successfully joined the workspace. Redirecting...
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="max-w-md w-full">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 size-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle>Join {invite?.workspaceName}</CardTitle>
                    <CardDescription>
                        You've been invited to join this workspace as a{' '}
                        <span className="font-medium text-foreground">{invite?.role}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {error && (
                        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                            {error}
                        </div>
                    )}

                    <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                        {invite?.companyName && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Company</span>
                                <span className="font-medium">{invite.companyName}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Invited by</span>
                            <span className="font-medium">{invite?.invitedBy}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Your role</span>
                            <span className="font-medium capitalize">{invite?.role}</span>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={handleDecline}
                            disabled={accepting}
                        >
                            Decline
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={handleAccept}
                            disabled={accepting}
                        >
                            {accepting ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="h-4 w-4 mr-2" />
                            )}
                            Accept Invite
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
